import { LoadMore } from 'components/atoms/LoadMore';
import React, { useCallback } from 'react';
import { DataTable } from '@getro/rombo';
import moment from 'moment';
import PropTypes from 'prop-types';
import { Building2, Contact, Globe, KeyRound, Lock } from 'lucide-react';
import { Flex, Box, Text } from 'rebass/styled-components';
import { Link } from 'react-router-dom';

import { SkeletonRow } from '../../components/atoms/rowSkeleton';
import { SharedListsActions } from '../../components/molecules/sharedListsActions';

export const getCompaniesColumns = () => [
  {
    Header: 'List',
    accessor: 'name',
    width: '40%',
    disableSortBy: true,
  },
  {
    Header: 'Contacts',
    accessor: 'contacts',
    width: '120px',
    disableSortBy: true,
  },
  {
    Header: 'Location',
    accessor: 'location',
    width: '30%',
    disableSortBy: true,
  },
  {
    Header: 'Visibility',
    accessor: 'visibility',
    width: '160px',
    disableSortBy: true,
  },
  {
    Header: 'Created by',
    accessor: 'createdBy',
    width: '240px',
    disableSortBy: true,
  },
  {
    Header: 'Created on',
    accessor: 'createdOn',
    width: '170px',
    disableSortBy: false,
    sx: { pointerEvents: 'none' },
  },
  {
    Header: '',
    accessor: 'actions',
    width: '64px',
    disableSortBy: true,
  },
];

export const ListDataTable = ({
  sharedLists,
  isLoading,
  isFetching,
  pathname,
  isManager,
  loadMore,
  hasMore,
  queryParams,
}) => {
  const companiesColumns = getCompaniesColumns();
  const showSkeleton = isLoading && !isFetching;

  const createListRows = useCallback(
    () =>
      sharedLists.map((list) => {
        let shareIcon = Lock;
        let shareText = 'Internal';

        if (list.passwordProtected) {
          shareIcon = KeyRound;
          shareText = 'Shared';
        } else if (list.shareable) {
          shareIcon = Globe;
          shareText = 'Shared';
        }

        return {
          passwordProtected: list.passwordProtected,
          id: list.id,
          slug: list.slug,
          name: (
            <Flex width="100%" justifyContent="flex-start" fontSize="14px" color="text.dark" fontWeight={500} p="8px">
              {showSkeleton ? (
                <Flex sx={{ gap: '8px' }}>
                  <SkeletonRow width={16} />
                  <SkeletonRow width={160} />
                </Flex>
              ) : (
                <>
                  <Box
                    as={list.kind === 'people' ? Contact : Building2}
                    height="16px"
                    width="16px"
                    aria-hidden="true"
                    mr="8px"
                    sx={{ color: 'neutral.400', flexShrink: 0 }}
                  />
                  <Box
                    sx={{
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                      minWidth: 0,
                      flex: 1,
                    }}
                  >
                    {list.name}
                  </Box>
                </>
              )}
            </Flex>
          ),
          contacts: (
            <Flex width="100%" justifyContent="flex-end" fontSize="14px">
              {showSkeleton ? <SkeletonRow width={24} /> : list.contactsCount}
            </Flex>
          ),
          location: (
            <Flex width="100%" justifyContent="flex-start" fontSize="14px">
              {showSkeleton ? <SkeletonRow width={100} /> : <Text fontSize="14px">{list.folderPath || '/'}</Text>}
            </Flex>
          ),
          visibility: (
            <Flex width="100%" justifyContent="flex-start" sx={{ gap: '8px', alignItems: 'center' }}>
              {showSkeleton ? (
                <SkeletonRow width={80} />
              ) : (
                <>
                  <Box size="16px" as={shareIcon} sx={{ color: 'neutral.400', flexShrink: 0 }} />
                  <Box as="p" color="text.main" fontSize="14px">
                    {shareText}
                  </Box>
                </>
              )}
            </Flex>
          ),
          createdBy: (
            <Flex width="100%" justifyContent="flex-start" fontSize="14px">
              {showSkeleton ? <SkeletonRow width={80} /> : list.pointOfContact.fullName}
            </Flex>
          ),
          createdOn: (
            <Flex width="100%" justifyContent="flex-start" fontSize="14px" color="text.main">
              {showSkeleton ? <SkeletonRow width={160} /> : moment(list.createdAt).format('D MMM YYYY')}
            </Flex>
          ),

          actions: (
            <SharedListsActions
              list={list}
              isManager={isManager}
              triggerStyle={{ justifyContent: 'flex-end' }}
              showDuplicateList={list.kind !== 'companies'}
              queryParams={queryParams}
            />
          ),
        };
      }),
    [isManager, sharedLists, showSkeleton, queryParams],
  );

  return (
    <Flex flexDirection="column" flexGrow="1">
      <DataTable
        sx={{
          pb: 3,
          minWidth: '980px',
          height: ['600px', '500px', '100%'],
          width: ['1000px!important', '1000px!important', '100%!important'],
          flexGrow: 1,
        }}
        canSort
        initialSortBy={{ id: 'createdOn', desc: false }}
        virtuosoProps={{
          useWindowScroll: true,
          components: {
            // eslint-disable-next-line react/no-multi-comp
            Footer: () => (isFetching ? <LoadMore /> : null),
          },
        }}
        loadMore={loadMore}
        items={createListRows()}
        columnDefinition={companiesColumns}
        hasMore={hasMore}
        isLoadingMore={isFetching}
        emptyView={
          <Text pt="40px" sx={{ alignSelf: 'center' }}>
            No results for this search
          </Text>
        }
        isInitialized
        rowProps={({ original }) => ({
          as: Link,
          to: `${pathname}/${original.id}/contacts`,
          sx: {
            textDecoration: 'none!important',
            minHeight: '40px',
            borderBottom: '1px solid',
            borderBottomColor: 'border.subtle',
            transition: 'background-color ease 0.3s',
            py: '8px',
            cursor: 'pointer',
            '&:hover': {
              backgroundColor: 'neutral.20',
            },

            '& > div': {
              '& > div': {
                minHeight: '40px',
                py: '0px',

                '& > div': {
                  minHeight: '40px',
                  margin: 0,
                },
              },

              '& > div > div': {
                display: 'flex',
                alignItems: 'center',
              },
            },
          },
        })}
      />
    </Flex>
  );
};

ListDataTable.propTypes = {
  sharedLists: PropTypes.arrayOf(PropTypes.object),
  isFetching: PropTypes.bool,
  isLoading: PropTypes.bool,
  pathname: PropTypes.string.isRequired,
  isManager: PropTypes.bool.isRequired,
  loadMore: PropTypes.func.isRequired,
  hasMore: PropTypes.bool.isRequired,
  queryParams: PropTypes.object,
};

ListDataTable.defaultProps = {
  sharedLists: [],
  isFetching: false,
  isLoading: false,
  queryParams: undefined,
};
